import { z } from "zod";

export const citedFactSchema = z.object({
  text: z.string().trim().min(8).max(220),
  sourceIds: z.array(z.number().int().positive()).min(1).max(4),
});

export const eventBriefContentSchema = z.object({
  headline: z.string().trim().min(4).max(60),
  brief: z.string().trim().min(8).max(320),
  background: z.array(z.string().trim().min(12).max(260)).max(4).default([]),
  confirmedFacts: z.array(citedFactSchema).max(6).default([]),
  uncertainties: z.array(z.string().trim().min(6).max(180)).max(4).default([]),
  whyItMatters: z.string().trim().min(8).max(220).optional(),
});

export type EventBriefContent = z.infer<typeof eventBriefContentSchema>;

export interface BriefSource {
  sourceIndex: number;
  url: string;
  title: string;
  siteName: string;
  snippet: string;
  summary?: string;
  publishedAt?: Date;
  trustTier: number;
}

export type BriefStatus =
  | "queued"
  | "processing"
  | "succeeded"
  | "low_confidence"
  | "insufficient"
  | "failed";

export const ACTIVE_BRIEF_STATUSES = ["queued", "processing"] as const;
export const DISPLAYABLE_BRIEF_STATUSES = ["succeeded", "low_confidence"] as const;
export const TERMINAL_BRIEF_STATUSES = ["succeeded", "low_confidence", "insufficient", "failed"] as const;

export function isActiveBriefStatus(status: string): boolean {
  return (ACTIVE_BRIEF_STATUSES as readonly string[]).includes(status);
}

export function isDisplayableBriefStatus(status: string): boolean {
  return (DISPLAYABLE_BRIEF_STATUSES as readonly string[]).includes(status);
}

export function isTerminalBriefStatus(status: string): boolean {
  return (TERMINAL_BRIEF_STATUSES as readonly string[]).includes(status);
}

export interface PublicBriefSource {
  id: number;
  url: string;
  title: string;
  siteName: string;
  publishedAt: string | null;
  cited: boolean;
}

export interface PublicTopicBrief {
  id: string;
  topicId: number;
  status: BriefStatus;
  triggerType: string;
  content: EventBriefContent | null;
  sources: PublicBriefSource[];
  sourceCount: number;
  qualityScore: number;
  generatedAt: string | null;
  updatedAt: string;
  progress: {
    stage: "queued" | "searching" | "generating" | "completed";
    percent: number;
    label: string;
  };
  error?: string;
}

export function buildLowConfidenceContent(topicName: string, reason: "no_evidence" | "generation_failed"): EventBriefContent {
  const reasonText = reason === "no_evidence"
    ? "当前公开检索结果不足以可靠解释这个词条"
    : "本次资料整理未能形成可核验的完整结论";
  return {
    headline: `${topicName}：背景资料仍待补充`.slice(0, 60),
    brief: `${reasonText}。为避免把无关内容拼成结论，暂不提供未经证实的细节；目前只能确认该词条曾进入微博热搜，具体起因与后续进展仍待有效来源补充。`.slice(0, 320),
    background: [],
    confirmedFacts: [],
    uncertainties: ["该词条的具体背景、涉及对象及事件进展暂缺少可交叉核验的公开资料。"],
  };
}
